/**
 * Companion Subscription
 * ----------------------------------------------------------------------------------------------------
 * Powers the "Subscribe" option on a non-subscribable primary product (e.g. a starter kit).
 * Choosing Subscribe adds TWO independent line items to the cart in one request:
 *   1. the selected primary variant  — one-time (no selling_plan)
 *   2. its linked companion variant  — on the chosen subscription selling_plan
 *
 * Config comes from the [data-companion-subscribe-map] JSON blob rendered by
 * snippets/barkco-product-info.liquid (primary variant id -> companion variant id, prices, blended %).
 * Generic & reusable: no product-specific logic lives here.
 *
 * SOFT-NAV SAFE: this file is loaded once globally (layout/theme.liquid). The variation-products
 * picker swaps `.section--barkco-product-info` via innerHTML (which never executes injected <script>s)
 * and fires `loadingStateChanged`. So we attach DOCUMENT-level listeners once, read the map/state
 * FRESH from the DOM on every interaction, and re-sync the display on `loadingStateChanged`.
 */
(function () {
  const LOG = "[companion-subscribe]";

  // Guard: attach the document-level listeners only once, even if this script is included twice.
  if (window.__companionSubscribeInit) return;
  window.__companionSubscribeInit = true;

  // --- Fresh reads (the map element is replaced on every soft-nav) ---
  const getMapEl = () => document.querySelector("[data-companion-subscribe-map]");

  const getMap = () => {
    const el = getMapEl();
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error(`${LOG} could not parse map`, error);
      return null;
    }
  };

  const getDiscountCode = () => {
    const el = getMapEl();
    return el ? el.getAttribute("data-discount-code") : null;
  };

  const isSubscribeSelected = () =>
    !!document.querySelector('.purchase-type-button.selected[data-purchase-type="subscribe"]');

  const getSelectedSellingPlanId = () => {
    const select = document.getElementById("selling-plan-select");
    return select && select.value ? select.value : null;
  };

  // Resolve a named control the way the browser submits it: form.elements includes controls
  // associated via the `form=` attribute even when they live OUTSIDE the <form> (the quantity input
  // and the variant picker's master selector both do). Prefer an enabled control with a value.
  const getFormControl = (form, name) => {
    if (!form || !form.elements) return null;
    const found = form.elements[name];
    if (!found) return null;
    if (typeof found.length === "number" && !found.tagName) {
      const list = Array.from(found);
      return (
        list.find((el) => !el.disabled && el.value) || list.find((el) => el.value) || list[0]
      );
    }
    return found;
  };

  const getPrimaryVariantId = (form) => {
    const el = getFormControl(form, "id") || document.querySelector('[name="id"]');
    return el && el.value ? el.value : null;
  };

  const getQuantity = (form) => {
    const el = getFormControl(form, "quantity") || document.querySelector('input[name="quantity"]');
    const value = el ? parseInt(el.value, 10) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const setText = (selector, value) => {
    const el = document.querySelector(selector);
    if (el && value != null) el.textContent = value;
  };

  // --- Keep the size-dependent display (card prices, savings %, recurring-note price) in sync ---
  const updateDisplayForVariant = (variantId) => {
    const map = getMap();
    const entry = map && variantId ? map[variantId] : null;
    if (!entry) return;

    setText("[data-companion-combined-price]", entry.combinedPriceMoney);
    setText("[data-companion-combined-was]", entry.combinedWasMoney);
    setText("[data-companion-buyonce-price]", entry.buyOncePriceMoney);
    const savings = document.querySelector("[data-companion-savings]");
    if (savings && typeof entry.blendedPct === "number") {
      savings.textContent = entry.blendedPct > 0 ? `Total ${entry.blendedPct}% Savings on First Order` : "";
    }
    setText("[data-companion-price]", entry.companionPriceMoney);
  };

  const syncCadenceText = () => {
    const select = document.getElementById("selling-plan-select");
    if (!select) return;
    const cadenceEl = document.querySelector("[data-companion-cadence]");
    const option = select.options[select.selectedIndex];
    const label = option && option.getAttribute("data-cadence-label");
    if (cadenceEl && label) cadenceEl.textContent = `every ${label}`;
  };

  const initDisplay = () => {
    if (!getMapEl()) return; // not a companion product (or not yet swapped in)
    const variantId = getPrimaryVariantId();
    if (variantId) updateDisplayForVariant(variantId);
    syncCadenceText();
  };

  // --- Two-item add ---
  const performTwoItemAdd = async (form, primaryVariantId, entry, sellingPlanId) => {
    const quantity = getQuantity(form);
    const discountCode = getDiscountCode();

    // Send the refill line bare (id + quantity + selling_plan), exactly like the refill's own PDP
    // add. OrderGroove adds _subscription/_selling_plan/_product_type server-side and applies the
    // subscribe & save incentive. Do NOT send _subscriber_type — it makes OG treat the shopper as an
    // existing subscriber and withholds the new-subscription discount.
    const items = [
      { id: Number(primaryVariantId), quantity },
      { id: Number(entry.companionVariantId), quantity, selling_plan: Number(sellingPlanId) },
    ];

    const sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    const sections = sectionsToBundle.join(",");
    const sectionsUrl = `${Shopify.routes.root}variants/${primaryVariantId}`;

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: JSON.stringify({ items, sections, sectionsUrl }),
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const responseJson = await response.json();

      if (response.ok) {
        // Attach the bundle discount code to the cart session so it carries to checkout.
        if (discountCode) {
          try {
            const redirectTo = `${Shopify.routes.root}cart.js`;
            await fetch(
              `${Shopify.routes.root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(redirectTo)}`,
              { headers: { "X-Requested-With": "XMLHttpRequest" } }
            );
          } catch (e) {
            console.warn(`${LOG} could not apply discount code "${discountCode}"`, e);
          }
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent["sections"] = responseJson["sections"];
        document.documentElement.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
              cart: cartContent,
            },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent: "variant:add", cart: cartContent },
          })
        );
      } else {
        console.error(`${LOG} /cart/add.js rejected`, responseJson);
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:error", { bubbles: true, detail: { error: responseJson["description"] } })
        );
      }
    } catch (error) {
      console.error(`${LOG} add failed`, error);
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:error", { bubbles: true, detail: { error: error.message } })
      );
    }
  };

  // ============================ Document-level listeners (attached once) ============================

  // Size / variant switch -> refresh card + note figures.
  document.addEventListener("variant:change", (event) => {
    const variant = event.detail && event.detail.variant;
    if (variant && variant.id != null) updateDisplayForVariant(String(variant.id));
  });

  // Cadence dropdown change -> refresh the recurring-note "every …" text (delegated).
  document.addEventListener("change", (event) => {
    if (event.target && event.target.id === "selling-plan-select") syncCadenceText();
  });

  // ATC submit -> two-item add, preempting the standard single-item submit. Capture phase so we run
  // before ProductForm._onSubmit; map is read fresh so this works after any soft-nav.
  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest('button[type="submit"], input[type="submit"]');
      if (!button) return;

      const form = button.form || button.closest("form");
      if (!form) return;
      if (!(form.getAttribute("action") || "").includes("/cart/add")) return;

      const map = getMap();
      if (!map) return; // not a companion product
      if (!isSubscribeSelected()) return; // Buy Once -> let the normal single-item add run

      const primaryVariantId = getPrimaryVariantId(form);
      const entry = primaryVariantId ? map[primaryVariantId] : null;
      const sellingPlanId = getSelectedSellingPlanId();

      if (!entry) {
        console.warn(
          `${LOG} no companion mapping for variant ${primaryVariantId} — falling back to single-item add.`
        );
        return;
      }
      if (!sellingPlanId) {
        console.warn(`${LOG} no selling plan selected — falling back to single-item add.`);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      button.setAttribute("aria-busy", "true");
      button.setAttribute("disabled", "disabled");
      performTwoItemAdd(form, primaryVariantId, entry, sellingPlanId).finally(() => {
        button.removeAttribute("aria-busy");
        button.removeAttribute("disabled");
      });
    },
    true
  );

  // Re-sync the display after a soft-navigation (variation-products picker) and on first load.
  document.addEventListener("loadingStateChanged", () => window.setTimeout(initDisplay, 100));
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDisplay);
  } else {
    initDisplay();
  }
})();
